import { io, Socket } from 'socket.io-client'
import { getAccessToken } from '../utils/tokenStorage'

let socketInstance: Socket | null = null

/**
 * Resolves the Socket.io server connection URL.
 * Defaults to port 3001 as configured in the backend configuration.
 */
export function getSocketServerUrl(): string {
  if (import.meta.env.VITE_SOCKET_URL) {
    return import.meta.env.VITE_SOCKET_URL
  }

  const apiBase = import.meta.env.VITE_API_BASE_URL
  if (apiBase) {
    try {
      const parsed = new URL(apiBase)
      if (parsed.hostname === 'localhost' || parsed.hostname === '127.0.0.1') {
        return `${parsed.protocol}//${parsed.hostname}:3001`
      }
      return `${parsed.protocol}//${parsed.hostname}${parsed.port ? `:${parsed.port}` : ''}`
    } catch {
      // ignore parsing error and fallback
    }
  }

  return 'http://localhost:3001'
}

/**
 * Initializes and connects the Socket.io client using the active JWT access token.
 */
export function connectSocket(): Socket | null {
  const token = getAccessToken()
  if (!token) {
    return null
  }

  if (socketInstance && socketInstance.connected) {
    return socketInstance
  }

  const serverUrl = getSocketServerUrl()

  socketInstance = io(serverUrl, {
    path: '/socket.io',
    transports: ['websocket', 'polling'],
    auth: {
      token,
    },
    reconnection: true,
    reconnectionAttempts: 10,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
    timeout: 10000,
  })

  socketInstance.on('connect', () => {
    if (import.meta.env.DEV) {
      console.log(`[realtime] Socket connected to ${serverUrl} (id: ${socketInstance?.id})`)
    }
  })

  socketInstance.on('connect_error', (err) => {
    if (import.meta.env.DEV) {
      console.warn(`[realtime] Socket connection error: ${err.message}`)
    }
  })

  socketInstance.on('disconnect', (reason) => {
    if (import.meta.env.DEV) {
      console.log(`[realtime] Socket disconnected: ${reason}`)
    }
  })

  return socketInstance
}

/**
 * Returns the active socket instance or attempts to connect if not yet initialized.
 */
export function getSocket(): Socket | null {
  if (!socketInstance) {
    return connectSocket()
  }
  return socketInstance
}

/**
 * Disconnects and destroys the active socket instance (e.g. on user logout).
 */
export function disconnectSocket(): void {
  if (socketInstance) {
    socketInstance.removeAllListeners()
    socketInstance.disconnect()
    socketInstance = null
    if (import.meta.env.DEV) {
      console.log('[realtime] Socket disconnected and cleaned up')
    }
  }
}

/**
 * Updates the authentication token when refreshed.
 */
export function updateSocketAuthToken(token: string): void {
  if (socketInstance) {
    socketInstance.auth = { token }
    if (!socketInstance.connected) {
      socketInstance.connect()
    }
  } else {
    connectSocket()
  }
}

/**
 * Helper to subscribe to a realtime socket event with automatic cleanup function.
 */
export function subscribeToRealtimeEvent<T = unknown>(
  eventName: string,
  callback: (payload: T) => void
): () => void {
  const socket = getSocket()
  if (!socket) {
    return () => {}
  }

  socket.on(eventName, callback)
  return () => {
    socket.off(eventName, callback)
  }
}
